import { useState, useEffect, useCallback } from 'react';
import type React from 'react';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';
import { useSideMenuContext } from 'components/shared/side-menu/side-menu-layout';

import { useCurrentUser } from 'state/current-user/current-user.thunk';
import { useImpersonation } from 'hooks/use-impersonation';

import useOnboardingPosition from '../hooks/use-onboarding-position';

import {
  TERMS_URL,
  ONBOARDING_KEY_PREFIX,
  SIDEBAR_ITEM_SELECTOR,
} from '../ai-assistant.constants';

import Icons from 'assets/icons';

const OnboardingPopup = (): React.JSX.Element | null => {
  const { data: currentUser } = useCurrentUser();
  const { isCollapsed } = useSideMenuContext();
  const { isImpersonating } = useImpersonation();

  const userId = currentUser?.oktaUserId;
  const isAdmin = currentUser?.fdUser || currentUser?.clientAdmin;

  const [visible, setVisible] = useState(false);
  const anchor = useOnboardingPosition(SIDEBAR_ITEM_SELECTOR, isCollapsed, {
    enabled: !isAdmin && visible,
  });

  useEffect(() => {
    if (!userId) return;

    const key = `${ONBOARDING_KEY_PREFIX}-${userId}`;
    const isDismissed = localStorage.getItem(key) === 'true';

    setVisible(!isDismissed);
  }, [userId]);

  const handleDismiss = useCallback(() => {
    if (userId && !isImpersonating()) {
      localStorage.setItem(`${ONBOARDING_KEY_PREFIX}-${userId}`, 'true');
    }

    setVisible(false);
  }, [userId, isImpersonating]);

  if (isAdmin || !visible || !anchor) return null;

  return (
    <div
      className="fixed z-50 flex items-start animate-fade-in transition-[left] duration-300 ease-out"
      style={{ top: anchor.top, left: anchor.left }}
    >
      <div className="flex items-center h-10 shrink-0">
        <div className="w-0 h-0 border-y-8 border-y-transparent border-r-8 border-r-gray-800" />
      </div>

      <div className="bg-gray-800 rounded-xl px-8 py-6 relative max-w-[400px] shadow-lg">
        <IconButton
          variant="neutral-text"
          size="baby"
          className="!absolute right-2 top-2 !text-white/60 hover:!bg-transparent"
          icon={<Icons.Close />}
          onClick={handleDismiss}
          aria-label="Close onboarding popup"
          data-testid="onboarding-close"
        />

        <div className="flex flex-col gap-2">
          <h3 className="text-white text-lg font-bold leading-7 pr-6">
            Meet MARK — Your on-demand analyst.
          </h3>
          <p className="text-white text-sm leading-5">
            Ask me your data questions and I&apos;ll keep you informed on the
            metrics that matter most to your success. By continuing, you agree
            to our updated{' '}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-lime-200 font-semibold underline"
            >
              Terms and Conditions
            </a>
            .
          </p>
        </div>

        <div className="mt-4">
          <Button
            variant="brand-filled"
            size="xs"
            iconBefore={<Icons.CheckCircle className="w-4 h-4" />}
            onClick={handleDismiss}
            data-testid="onboarding-got-it"
          >
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPopup;
